import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Shared chrome for every Settings sub-page (Bot / Widget / Domains).
 * Centralizes the page header and the sticky save bar so the three pages
 * look like siblings without re-implementing the layout.
 *
 * Pages use the SettingsLayout > FormSection > FormRow pattern. Each row
 * pairs a label + hint on the left with the input/control on the right.
 */

interface SettingsLayoutProps {
  title: string;
  description: string;
  /** Right-aligned action(s) in the header, e.g. an "Open widget preview" button. */
  headerRight?: ReactNode;
  /** Sticky save bar pinned to the bottom of the page. Pass null to hide. */
  saveBar?: ReactNode;
  children: ReactNode;
}

export function SettingsLayout({ title, description, headerRight, saveBar, children }: SettingsLayoutProps) {
  return (
    <div className="space-y-6 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium text-text-strong">{title}</h1>
          <p className="mt-1 max-w-2xl text-sm text-text-soft">{description}</p>
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </div>

      <div className="space-y-6">{children}</div>

      {saveBar && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/90 backdrop-blur-sm">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 md:px-8">
            {saveBar}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormSection — a card containing related rows.
// ---------------------------------------------------------------------------

interface FormSectionProps {
  title: string;
  description?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <section className="card">
      <header className="border-b border-line px-5 py-4">
        <h2 className="text-md font-medium text-text-strong">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-text-soft">{description}</p>}
      </header>
      <div className="divide-y divide-line">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FormRow — label on the left, control on the right.
// ---------------------------------------------------------------------------

interface FormRowProps {
  label: string;
  hint?: ReactNode;
  htmlFor?: string;
  /** Use "stacked" for controls that want full width (textareas, sandboxes). */
  layout?: "split" | "stacked";
  children: ReactNode;
  className?: string;
}

export function FormRow({ label, hint, htmlFor, layout = "split", children, className }: FormRowProps) {
  if (layout === "stacked") {
    return (
      <div className={cn("px-5 py-4", className)}>
        <label htmlFor={htmlFor} className="block text-sm font-medium text-text-strong">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-xs text-text-soft">{hint}</p>}
        <div className="mt-3">{children}</div>
      </div>
    );
  }
  return (
    <div className={cn("grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-[1fr_2fr] sm:items-start", className)}>
      <div>
        <label htmlFor={htmlFor} className="block text-sm font-medium text-text-strong">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-xs text-text-soft">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}
